/**
 * AuthModal - Modal de autenticación
 * 
 * Este componente muestra el modal de login/registro/magic-link.
 * Ahora consume directamente el AuthProvider para determinar si hay sesión.
 * 
 * SIN listeners adicionales de onAuthStateChange.
 * SIN delays arbitrarios.
 */
import React from 'react';
import { useAuth } from '../AuthProvider';
import { useAuthModalState } from '../useAuthModalState';
import { hasSupabaseEnv, supabase } from '../../lib/supabaseClient';
import { useNavigate, useLocation } from 'react-router-dom';
import { Tabs } from './Tabs';
import { SocialProviders } from './SocialProviders';
import { LoginForm } from './forms/LoginForm';
import { RegisterForm } from './forms/RegisterForm';
import { MagicLinkForm } from './forms/MagicLinkForm';
import { ResetPasswordForm } from './forms/ResetPasswordForm';
import { useTranslation } from 'react-i18next';
import { LanguageSelect } from './components/LanguageSelect';
import { safeLocalStorage } from '../../lib/safeBrowserStorage';

export function AuthModal() {
  const { open, view, setView, close, loading, setLoading, message, setMessage, error, setError, redirectTo, origin } = useAuthModalState();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Usar AuthProvider unificado
  const { isAuthenticated, user, sessionUser } = useAuth();
  
  const { t } = useTranslation();
  const pathname = location.pathname;
  
  // forceLock: el modal no se puede cerrar si estamos en /user sin autenticar
  const forceLock = (origin === 'user-panel' || redirectTo === '/user') && pathname === '/user' && !isAuthenticated;
  
  // Estado para verificación de email (solo visual, no listener)
  const [needsVerification, setNeedsVerification] = React.useState(false);
  const openedAtRef = React.useRef(0);

  // Verificar estado de email cuando cambia el usuario
  React.useEffect(() => {
    if (sessionUser?.email) {
      setNeedsVerification(!sessionUser.email_confirmed_at);
    } else {
      setNeedsVerification(false);
    }
  }, [sessionUser?.email, sessionUser?.email_confirmed_at]);

  // Persistir pestaña previa al abrir
  React.useEffect(() => {
    if (open) {
      const stored = safeLocalStorage.getItem('authModal.lastView');
      if (stored && ['login', 'register', 'magic', 'reset'].includes(stored)) {
        setView(stored);
      }
    }
  }, [open, setView]);

  // Guardar cambios de pestaña
  React.useEffect(() => {
    if (view) safeLocalStorage.setItem('authModal.lastView', view);
  }, [view]);

  // Scroll lock body
  React.useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    openedAtRef.current = Date.now();
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  // Cerrar modal si el usuario navega fuera de la ruta protegida
  React.useEffect(() => {
    if (!open) return;
    if (!isAuthenticated && pathname !== '/user' && origin === 'user-panel') {
      close();
    }
  }, [pathname, open, isAuthenticated, origin, close]);

  // ✅ SIMPLIFICADO: Cerrar modal cuando isAuthenticated cambia a true
  React.useEffect(() => {
    if (open && isAuthenticated) {
      // Navegar a redirectTo si aplica
      if (redirectTo && pathname !== redirectTo) {
        navigate(redirectTo, { replace: false });
      }
      close();
    }
  }, [open, isAuthenticated, redirectTo, navigate, pathname, close]);

  // Escape para cerrar
  React.useEffect(() => {
    const esc = (e) => { if (e.key === 'Escape' && !forceLock) close(); };
    if (open) window.addEventListener('keydown', esc);
    return () => window.removeEventListener('keydown', esc);
  }, [open, close, forceLock]);

  // Banner de variables de entorno
  const [hideEnvBanner, setHideEnvBanner] = React.useState(false);
  const varsOk = hasSupabaseEnv || hideEnvBanner;

  const sidebarWidth = 256;

  const handleClose = () => {
    if (forceLock) return;
    if (openedAtRef.current && (Date.now() - openedAtRef.current) < 180) {
      // Evitar cierre inmediato por clic que también disparó la apertura
      return;
    }
    // Si estamos en /user sin login y el modal se cierra manualmente, redirigimos a home para no dejar página vacía.
    if(pathname === '/user' && !isAuthenticated) {
      navigate('/', { replace:false });
    }
    close();
  };

  // El contenedor global ahora ignora eventos (pointer-events-none) para permitir interacción
  // con el contenido subyacente (sidebar) excepto en las áreas donde colocamos backdrops
  // específicos con pointer-events-auto.
  const overlayClass = "fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none";
  const allowSidebar = !isAuthenticated && pathname === '/user';

  // Cálculo para centrar el modal en el espacio disponible a la derecha del sidebar
  // Espacio disponible = viewportWidth - sidebarWidth. Queremos colocar el modal
  // de modo que su centro coincida con sidebarWidth + (availableWidth / 2).
  // Cálculo simple (sin useMemo para evitar hook condicional tras early return) — costo despreciable.
  const centerStyle = allowSidebar ? { paddingLeft: sidebarWidth + 'px' } : {};

  if(!open) return null;

  return (
    <div className={overlayClass} style={centerStyle} data-testid="auth-modal-overlay">
      {allowSidebar ? (
        <>
          {/* Zona derecha oscurecida (contenido principal) */}
          <div
            className="absolute top-0 right-0 bottom-0 bg-black/60 backdrop-blur-sm pointer-events-auto"
            style={{ left: sidebarWidth }}
            onClick={forceLock ? undefined : handleClose}
            data-testid="auth-modal-backdrop"
            aria-hidden="true"
          />
        </>
      ) : (
        <div
          className="absolute inset-0 bg-black/60 backdrop-blur-sm pointer-events-auto"
          onClick={forceLock ? undefined : handleClose}
          data-testid="auth-modal-backdrop"
          aria-hidden="true"
        />
      )}
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Authentication"
        className={"relative z-10 bg-dark-light border border-gray-700 rounded-xl w-full max-w-md p-6 shadow-xl pointer-events-auto " + (allowSidebar ? 'mx-auto' : '')}
        data-testid="auth-modal"
      >
        {/* Eliminado el selector superpuesto para no interferir con la pestaña Magic Link */}
        {forceLock && (
          <div className="absolute -top-8 left-1/2 -translate-x-1/2 text-center text-[11px] text-gray-400 pointer-events-none select-none">
            {t('auth.protected_route', 'Ruta protegida: inicia sesión para continuar')}
          </div>
        )}
        {!isAuthenticated && !forceLock && (
          <div className="mb-4 flex items-center justify-center gap-2 bg-primary/10 border border-primary/20 rounded-lg px-4 py-2.5">
            <span className="text-base">✨</span>
            <span className="text-sm text-gray-300">
              <span className="font-semibold text-primary">{t('auth.promo.subscribe', 'Suscríbete gratis')}</span>
              {' · '}{t('auth.promo.trial', 'Prueba la plataforma 7 días')}
              {' · '}{t('auth.promo.nocard', 'Sin tarjeta de crédito')}
            </span>
          </div>
        )}
  <Tabs view={view} setView={setView} close={handleClose} />
        {!varsOk && (
          <div className="mb-4 text-xs bg-warning/20 border border-warning/40 text-warning px-3 py-2 rounded relative pr-8">
            <button onClick={()=>setHideEnvBanner(true)} className="absolute top-1 right-1 text-warning/70 hover:text-warning text-[10px]" aria-label={t('common.close','Close')}>✕</button>
            {t('auth.env.missing', 'Variables de Supabase no detectadas. Define REACT_APP_SUPABASE_URL/REACT_APP_SUPABASE_ANON_KEY o VITE_SUPABASE_URL/VITE_SUPABASE_ANON_KEY y reinicia el servidor.')}
          </div>
        )}
  <SocialProviders setError={setError} setMessage={setMessage} setView={setView} />
  {view==='login' && <LoginForm setView={setView} setError={setError} setMessage={setMessage} close={handleClose} setLoading={setLoading} />}
  {view==='register' && <RegisterForm setView={setView} setError={setError} setMessage={setMessage} setLoading={setLoading} />}
  {view==='magic' && <MagicLinkForm setView={setView} setError={setError} setMessage={setMessage} setLoading={setLoading} />}
  {view==='reset' && <ResetPasswordForm setView={setView} setError={setError} setMessage={setMessage} setLoading={setLoading} />}
    {/* Barra inferior con selector de idioma (izquierda) y sin alterar botones de cada formulario */}
    <div className="mt-4 pt-3 border-t border-gray-700 flex items-center justify-between">
      <LanguageSelect />
      {/* El área derecha la ocupan los botones propios de cada formulario; no añadimos botones aquí para no duplicar */}
      <div className="text-[10px] text-transparent select-none">.</div>
    </div>
    {needsVerification && (
      <div className="mt-3 text-xs bg-warning/20 border border-warning/40 text-warning px-3 py-2 rounded">
        {t('auth.verification.unverified','Tu correo no está verificado.')} <button onClick={async()=>{ try{ await supabase.auth.resend({ type:'signup' }); setMessage(t('auth.verification.resent','Correo de verificación reenviado')); } catch(e){ setError(e.message||t('auth.errors.resend_failed','No se pudo reenviar')); } }} className="underline hover:text-yellow-300">{t('auth.resend','Reenviar')}</button>
      </div>
    )}
        {(message || error) && <div className={`mt-4 text-sm ${error? 'text-red-400':'text-green-400'}`}>{error||message}</div>}
        {loading && <div className="absolute inset-0 rounded-xl bg-black/40 flex items-center justify-center text-primary text-sm">{t('common.loading','Procesando…')}</div>}
      </div>
    </div>
  );
}
